/**
 * @author omriha
 * 05/02/2020
 */
import React, {useState} from "react";
import {connect, useDispatch} from "react-redux";
import {push} from "connected-react-router";
import {useTranslation} from "react-i18next";
import queryString from "query-string";
import SMCPage from "../../../components/core/pageComponents/SMCPage";
import ButtonContainer from "../../../components/Simple/ButtonContainer";
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import SMCButton from '../../../components/Simple/SMCButton';
import {useCommunityEditor} from './CommunityEditorHelper';
import {SMCTab, SMCTabsContainer} from '../../../components/core/SMCTabs';
import CommunityMembers from './CommunityMembers';
import SMCNote from '../../../components/Simple/SMCNote';
import SMCIcon from '../../../components/Simple/SMCIcon';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCLabel from '../../../components/Simple/SMCLabel';
import {CommunityGetSchema} from '../../../api/DBSchema/Community';
import {communityPermissionsSelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import VerifyButtonsContainer from "../VerifyButtonsContainer";
import {setShowBackButton} from "../../../store/actions/view.actions";
import ButtonsContainerStyles from "../ButtonsContainerStyles";


/**
 * High level element to fetch, edit and save community, all logic implemented inside.
 * @param {string} id Id of community to fetch from the server to edit
 */
function CommunityEditor({ navigateTo, id, urlParams, communityPermissions }) {
  const { t } = useTranslation();
  const { editorWidth, editorHeight } = useEditorDimensions(true);

  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(setShowBackButton(true,t("SMC_COMMUNITIES_EDITOR_PAGENAME")));
    return () => {
      dispatch(setShowBackButton(false, ""));
    };
  }, [dispatch, t]);

  const MEMBER_HELP_ID = "3755";

  const [currentlySelectedTab, setCurrentlySelectedTab] = useState("CONFIGURE");
  const classes = ButtonsContainerStyles();

  const modifyPermission = communityPermissions
    ? communityPermissions.modify
    : true;
  const deletePermission = communityPermissions
    ? communityPermissions.delete
    : true;

  let defaultPage = "general";
  const queryParams = queryString.parse(urlParams);
  if (queryParams && queryParams["page"]) {
    defaultPage = queryParams["page"];
  }

  const {
    communityObject,
    isObjectDirty,
    editorPages,
    helpId,
    isPageDataLoaded,
    isInnerPageLoading,
    isPageLoadFailed,
    chosenPageId,
    errorMessagesByPageList,
    onPageChange,
    setCommunityData,
    saveButtonOnClick,
    deleteButtonOnClick,
    revertButtonOnClick,
    reloadPageData,
  } = useCommunityEditor(id, defaultPage);

  // Data passed down to the editor pages
  const editorPageData = {
    communityObject,
    setEditorPageId: onPageChange,
    setCommunityData,
    isPageDataLoaded,
  };

  const topologyError = communityObject
    ? communityObject[CommunityGetSchema.topologyError]
    : undefined;
  const _helpId =
    currentlySelectedTab === "CONFIGURE" ? helpId : MEMBER_HELP_ID;
  const _height = editorHeight - (topologyError ? 30 : 0);

    const editorTabContent = (
        <div className="ObjectEditor Tabs" style={{ width: editorWidth }}>
            {/* Header */}
            <div className="ObjectEditorHeader Tabs">
                <ButtonContainer>
                    <SMCButton
                        labelClassName={classes.labelColor}
                        icon="delete"
                        variant="ghost"
                        disabled={!isPageDataLoaded || !deletePermission}
                        onClick={deleteButtonOnClick}
                    >
                        {t('SMC_GEN_DELETE')}
                    </SMCButton>
                </ButtonContainer>
                <LabelContainer label="Name:" value={id} />
            </div>
            <SMCDisabledProvider forceDisable={!modifyPermission}>
                <SMCEditor
                    editorPageData={editorPageData}
                    height={_height}
                    width={editorWidth}
                    errorListByPage={errorMessagesByPageList}
                    chosenPageId={chosenPageId}
                    onPageChange={onPageChange}
                    isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                    isPageLoadFailed={isPageLoadFailed}
                    reloadData={reloadPageData}
                    pages={editorPages}
                />
            </SMCDisabledProvider>
        </div>
    );

  const membersTabContent = (
    <SMCDisabledProvider forceDisable={!modifyPermission}>
      <CommunityMembers width={editorWidth} height={_height} community={id} />
    </SMCDisabledProvider>
  );

  return (
    <>
      <RouteLeavingGuard when={isObjectDirty} />
      <SMCPage loading={!isPageDataLoaded} helpId={_helpId}>
        {topologyError && (
          <SMCNote variant="warning" style={{ width: editorWidth }}>
            <RowLayout spacing="8px">
              <SMCIcon icon="warning" />
              <SMCLabel inline bold>
                {topologyError}
              </SMCLabel>
            </RowLayout>
          </SMCNote>
        )}
        <SMCTabsContainer
          width={editorWidth}
          tabOnClick={setCurrentlySelectedTab}
        >
          <SMCTab title={t("SMC_COMMUNITY_TAB0")} id="CONFIGURE">
            {editorTabContent}
          </SMCTab>
          <SMCTab title={t("SMC_COMMUNITY_TAB1")} id="MEMBERS">
                    {membersTabContent}
                </SMCTab>
            </SMCTabsContainer>
        </SMCPage>
        <VerifyButtonsContainer
            disabled={!isPageDataLoaded || !modifyPermission}
            onClickSaveButton={saveButtonOnClick}
            onClickCancelButton={revertButtonOnClick}
        />
    </>
  );
}

const mapStateToProps = (state) => ({
  communityPermissions: communityPermissionsSelector(state),
  urlParams: state.router.location.search,
});

const mapDispatchToProps = {
  navigateTo: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommunityEditor);

